import { Flex, Form, Select, Typography } from "antd";
import { useAgents } from "@/common/artemis-operations/agents";
import type { AgentHealth, AgentRole } from "@/common/model";
import { AgentHealthBadge } from "./agent-badges";

interface AgentSelectorProps {
	workspaceId: string;
	onAgentChange: (agentId: string) => void;
	/** Whether agent selection is required (shows red validation state). */
	agentRequired?: boolean;
	fieldName?: string;
}

interface AgentOption {
	value: string;
	label: string;
	role?: AgentRole;
	health?: AgentHealth;
}

/**
 * Renders an agent selector. Only enabled agents are selectable (the API rejects assigning a
 * disabled agent anyway); an agent's health is shown but does not gate selection — an
 * unreachable-but-enabled agent can still be assigned and is dispatched once it's reachable.
 * Driven by the parent Form — value is injected by antd automatically.
 */
export function AgentSelector({
	workspaceId,
	onAgentChange,
	fieldName = "assigneeId",
	agentRequired = false,
}: AgentSelectorProps) {
	const { data: agents } = useAgents(workspaceId);

	const agentOptions: AgentOption[] = (agents ?? [])
		.filter((a): a is NonNullable<typeof a> => a?.status === "ENABLED")
		.map((a) => ({
			value: String(a.id),
			label: a.name || "Agent",
			role: a.role,
			// Null for External agents: the platform cannot reach them, so there is no health.
			health: a.health ?? undefined,
		}));

	return (
		<Form.Item
			name={fieldName}
			label="Agent"
			rules={[
				{
					required: agentRequired,
					message: "Please select an agent",
				},
			]}
		>
			<Select
				placeholder="Select an agent"
				options={agentOptions}
				onChange={onAgentChange}
				notFoundContent={
					<Typography.Text type="secondary">
						No agents available in this workspace
					</Typography.Text>
				}
				optionRender={(option) => (
					<Flex align="center" justify="space-between" gap={8}>
						<Typography.Text>{option.data.label}</Typography.Text>
						<Flex gap={4}>
							{option.data.health && (
								<AgentHealthBadge status={option.data.health} />
							)}
						</Flex>
					</Flex>
				)}
			/>
		</Form.Item>
	);
}
